import React, { useState, useEffect, FormEvent } from "react";
import PageHeader from "src/Components/Customer/PageHeader";
import PreviewProfileButton from "src/Components/Customer/PreviewProfileButton";
import SimpleInput from "src/Components/SimpleInput";
import ToggleRadioBtn from "src/Components/ToggleRadioBtn";
import PrimaryBtn from "src/Components/GlobalComponents/primaryBtn";
import { faPen, faSpinner, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IAvailability } from "src/types/GlobalInterfaces";
import { CURRENCY, weekDays } from "src/lib/constans";
import { capitalize } from "@mui/material";
import { toast } from "react-toastify";
import httpRequest from "src/helpers/httpRequest";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store/store";
import { updateDoctorField } from "../../../Store/doctor";
import { setUniqueData } from "src/Store/reducer";
import moment from "moment";

const emptyObj: IAvailability = {
  hospitalName: "",
  fees: "",
};

const HospitalAvailabilityEdit: React.FC = () => {
  const { profile } = useSelector((store: RootState) => store.doctor);
  const dispatch = useDispatch();
  const [isLoader, setIsLoader] = useState<boolean>(false);
  const [isTerms, setIsTerms] = useState<any>(true);

  const [timings, setTimings] = useState<IAvailability[]>([
    {
      hospitalName: "",
      fees: "",
    },
  ]);
  const [editId, setEditId] = useState<null | number>(null);
  const [createMode, setCreateMode] = useState(false);
  const [payload, setPayload] = useState(emptyObj);
  const [deleteId, setDeleteId] = useState<null | number>(null);
  const handleChangeItem = (key: string, val: string) => {
    setPayload((prev) => ({
      ...prev,
      [key]: val,
    }));
  };
  const editSetting = (obj: IAvailability, id: number) => {
    setEditId(id);
    setPayload(obj);
    setCreateMode(true);
  };
  const cancelEdit = () => {
    setEditId(null);
    setPayload(emptyObj);
    setCreateMode(false);
  };

  const handleDeleteRow = async (id: number) => {
    setDeleteId(id);
    const newArr = timings.filter((_, i) => i !== id);
    await APIRequest(newArr);
    setDeleteId(null);
  };

  const APIRequest = async (newArr: IAvailability[]) => {
    const { res, err } = await httpRequest<{
      message: string;
      availability: { timings: IAvailability[] };
      percent: number;
    }>({
      method: "post",
      path: "/provider/update-timing",
      params: { timings: newArr },
    });
    if (res) {
      const { message, availability, percent } = res;
      toast("Hospital availability updated", { type: "success" });
      setCreateMode(false);
      setPayload(emptyObj);
      setEditId(null);
      dispatch(updateDoctorField({ timings: availability.timings }));
      dispatch(
        setUniqueData({ key: "profileCompletion", value: String(percent) })
      );
    } else {
      toast(err, { type: "error" });
    }
  };

  const handleToggle = (day: keyof IAvailability) => {
    setPayload((prev) => {
      const isExist = Object.keys(payload).includes(day);
      if (isExist) {
        // Remove the day from the timing object
        const updatedTiming = { ...payload };
        delete updatedTiming[day];
        return updatedTiming;
      } else {
        return {
          ...prev,
          [day]: [["", ""]],
        };
      }
    });
  };

  const handleChange = (
    day: keyof IAvailability,
    index: number,
    type: number,
    value: string
  ) => {
    setPayload((prev) => {
      const currentSchedule = Array.isArray(prev[day])
        ? [...(prev[day] as [string, string][])]
        : [];

      const updatedSlots = currentSchedule.map((slot, slotIdx) =>
        slotIdx === index ? [...slot] : slot
      );
      updatedSlots[index][type] = value;

      const timeToMinutes = (time: string): string => {
        if (!time) return "0";
        const [hours, minutes] = time.split(":").map(Number);
        return `${hours * 60 + minutes}`;
      };

      // Function to convert minutes back to HH:mm format
      const minutesToTime = (minutes: string): string => {
        if (!minutes || minutes === "0") return "00:00";
        const totalMinutes = parseInt(minutes);
        const hours = Math.floor(totalMinutes / 60);
        const mins = totalMinutes % 60;
        return `${hours.toString().padStart(2, "0")}:${mins
          .toString()
          .padStart(2, "0")}`;
      };

      // Helper function to get intervals for a slot (handling wrap to midnight only)
      const getIntervals = (fromMin: number, toMin: number): [number, number][] => {
        if (toMin > fromMin) {
          return [[fromMin, toMin]]; // Normal same-day
        } else if (toMin === 0 && fromMin > 0) {
          return [[fromMin, 1440]]; // Wrap to exactly midnight (end at 24:00)
        } else {
          return []; // Invalid wrap
        }
      };

      // Helper function to check if two sets of intervals overlap
      const checkOverlap = (
        newIntervals: [number, number][],
        otherIntervals: [number, number][]
      ): boolean => {
        for (const ni of newIntervals) {
          for (const oi of otherIntervals) {
            if (Math.max(ni[0], oi[0]) < Math.min(ni[1], oi[1])) {
              return true;
            }
          }
        }
        return false;
      };

      // Check if 'To' time is valid relative to 'From' time
      const fromTime = updatedSlots[index][0];
      const toTime = updatedSlots[index][1];

      if (fromTime && toTime) {
        const fromMinutes = parseInt(timeToMinutes(fromTime));
        const toMinutes = parseInt(timeToMinutes(toTime));
        
        if (toMinutes === fromMinutes) {
          toast(
            `${capitalize(
              String(day)
            )}: Start and end times must be different.`,
            {
              type: "error",
            }
          );
          const prevState = currentSchedule.map((slot) => [...slot]);
          prevState[index][type] = "";
          return { ...prev, [day]: prevState };
        } else if (toMinutes < fromMinutes) {
          // Wrap-around only allowed if end is exactly 12:00 AM (00:00)
          if (toMinutes !== 0) {
            toast(
              `${capitalize(
                String(day)
              )}: For overnight shifts, end time can only be up to 12:00 AM.`,
              {
                type: "error",
              }
            );
            const prevState = currentSchedule.map((slot) => [...slot]);
            prevState[index][type] = "";
            return { ...prev, [day]: prevState };
          }
        }
        // Normal case (to > from) or valid wrap (to=0 < from) is fine here
      }

      // Validate: Check for overlapping time slots across all hospitals and within the same hospital
      if (fromTime && toTime) {
        const newFrom = parseInt(timeToMinutes(fromTime));
        const newTo = parseInt(timeToMinutes(toTime));
        const newIntervals = getIntervals(newFrom, newTo);
        if (newIntervals.length === 0) {
          // Shouldn't reach here due to earlier validation, but safety
          const prevState = currentSchedule.map((slot) => [...slot]);
          prevState[index][type] = "";
          return { ...prev, [day]: prevState };
        }

        // Collect existing slots from other hospitals
        const existingSlotsFromOtherHospitals = timings
          .filter((timing) => editId === null || !timing._id || timing._id !== payload._id)
          .flatMap((timing) =>
            Array.isArray(timing[day]) ? (timing[day] as [string, string][]) : []
          )
          .filter((slot) => slot[0] && slot[1]);

        // Collect other slots from current payload (for intra-hospital check)
        const otherCurrentSlots = currentSchedule.filter((_, i) => i !== index).filter((slot) => slot[0] && slot[1]);

        const allOtherSlots = [...existingSlotsFromOtherHospitals, ...otherCurrentSlots];

        for (const otherSlot of allOtherSlots) {
          const otherFrom = parseInt(timeToMinutes(otherSlot[0]));
          const otherTo = parseInt(timeToMinutes(otherSlot[1]));
          const otherIntervals = getIntervals(otherFrom, otherTo);
          if (otherIntervals.length > 0 && checkOverlap(newIntervals, otherIntervals)) {
            let message = "";
            let isIntra = otherCurrentSlots.some(
              (slot) => slot[0] === otherSlot[0] && slot[1] === otherSlot[1]
            );

            let conflictingHospital: string | null = null;
            if (isIntra) {
              conflictingHospital = payload.hospitalName || "";
              message = `overlaps with another time slot for ${conflictingHospital}`;
            } else {
              conflictingHospital = timings.find(
                (timing) =>
                  Array.isArray(timing[day]) &&
                  (timing[day] as [string, string][]).some(
                    (s) => s[0] === otherSlot[0] && s[1] === otherSlot[1]
                  )
              )?.hospitalName || null;
              if (conflictingHospital) {
                message = `overlaps with another slot for ${conflictingHospital}`;
              } else {
                continue;
              }
            }

            const formattedFrom = moment(
              otherSlot[0],
              "HH:mm"
            ).format("h:mm A");
            const formattedTo = moment(
              otherSlot[1],
              "HH:mm"
            ).format("h:mm A");
            toast(
              `${capitalize(
                String(day)
              )}: Time slot ${message} (${formattedFrom} - ${formattedTo}).`,
              { type: "error" }
            );
            const prevState = currentSchedule.map((slot, slotIdx) =>
              slotIdx === index ? [...slot] : slot
            );
            prevState[index][type] = "";
            return { ...prev, [day]: prevState };
          }
        }
      }

      // Validate: Check for conflicts with online availability (profile.availability)
      if (profile?.availability) {
        const onlineTimings = profile.availability[day];
        if (onlineTimings && Array.isArray(onlineTimings)) {
          const fromTime = updatedSlots[index][0];
          const toTime = updatedSlots[index][1];
          if (fromTime && toTime) {
            const newFrom = parseInt(timeToMinutes(fromTime));
            const newTo = parseInt(timeToMinutes(toTime));
            const newIntervals = getIntervals(newFrom, newTo);
            if (newIntervals.length === 0) return { ...prev, [day]: updatedSlots };

            for (const [onlineFromStr, onlineToStr] of onlineTimings) {
              const onlineFromMinutes = parseInt(timeToMinutes(onlineFromStr));
              const onlineToMinutes = parseInt(timeToMinutes(onlineToStr));
              // Assume online timings are same-day only (no wrap), so intervals [[onlineFrom, onlineTo]]
              const onlineIntervals: [number, number][] = onlineToMinutes > onlineFromMinutes 
                ? [[onlineFromMinutes, onlineToMinutes]] 
                : []; // Skip invalid online slots

              if (onlineIntervals.length > 0 && checkOverlap(newIntervals, onlineIntervals)) {
                const formattedOnlineFrom = moment(onlineFromStr, "HH:mm").format(
                  "h:mm A"
                );
                const formattedOnlineTo = moment(onlineToStr, "HH:mm").format(
                  "h:mm A"
                );
                toast(
                  `${capitalize(
                    String(day)
                  )}: Hospital time slot conflicts with online availability (${formattedOnlineFrom} - ${formattedOnlineTo}).`,
                  { type: "error" }
                );
                const prevState = currentSchedule.map((slot, slotIdx) =>
                  slotIdx === index ? [...slot] : slot
                );
                prevState[index][type] = "";
                return { ...prev, [day]: prevState };
              }
            }
          }
        }
      }

      return { ...prev, [day]: updatedSlots };
    });
  };

  const addRow = (day: keyof IAvailability) => {
    setPayload((prev) => {
      const currentSchedule = Array.isArray(payload[day])
        ? (payload[day] as [string, string][])
        : [];
      return {
        ...prev,
        [day]: [...currentSchedule, ["", ""]],
      };
    });
  };

  const deleteRow = (day: keyof IAvailability, i: number) => {
    setPayload((prev) => {
      // Ensure `timing[day]` is an array or default to an empty array
      const currentSchedule = Array.isArray(payload[day])
        ? (payload[day] as [string, string][])
        : [];
      // Filter out the item at the specified index
      const updatedSchedule = currentSchedule.filter(
        (_, slotIndex) => slotIndex !== i
      );
      return {
        ...prev,
        [day]: updatedSchedule,
      };
    });
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!isTerms) {
      toast(
        "You must agree to the platform commission deduction before proceeding."
      );
      return;
    }
    setIsLoader(true);
    const newArr =
      editId || editId === 0
        ? timings.map((v, i) => (i === editId ? payload : v))
        : [...timings, payload];
    await APIRequest(newArr);
    setIsLoader(false);
  };

  useEffect(() => {
    if (profile)
      setTimings(
        profile.timings && profile.timings.length
          ? profile.timings
          : [{ hospitalName: "", fees: "" }]
      );
    if (profile) {
      if (!(profile?.timings || [])?.length) {
        setCreateMode(true);
        setTimings([]);
      } else {
        setTimings(profile?.timings || []);
      }
    }
  }, [profile]);

  return (
    <>
      <PageHeader
        title="Availability"
        isButton={true}
        ButtonComponent={PreviewProfileButton}
      />

      <div className="parent-box">
        <div className="pt-2 pb-4">
          <p className="font-Poppins-SemiBold font-size-20px theme-color-green">
            Hospital
          </p>
          <p className="font-Poppins-Regular font-size-14px theme-color-gren">
            Keep this up to date so you get enquiries that suit you.
          </p>
        </div>

        {timings?.length
          ? timings.map((item: IAvailability, index: number) => (
              <div
                key={index}
                className={`eduction-work-his-change ${
                  editId === index ? "eduction-work-his-change-active" : ""
                }`}
              >
                <div className="absolute flex gap-4 right-4 top-4">
                  <FontAwesomeIcon
                    icon={faPen}
                    className="cursor-pointer font-size-20px text-[#707070]"
                    onClick={() => editSetting(item, index)}
                    id={`edit-hospital-availability-pen-icon-${index}`}
                  />
                  <FontAwesomeIcon
                    icon={deleteId === index ? faSpinner : faTrash}
                    spin={deleteId === index}
                    className="cursor-pointer font-size-20px text-[#707070]"
                    onClick={() => handleDeleteRow(index)}
                    id={`edit-hospital-availability-delete-icon-${index}`}
                  />
                </div>
                <div className="flex flex-col gap-5">
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    <div>
                      <span className="font-size-18px theme-color-green font-Poppins-Medium block">
                        Hospital Name
                      </span>
                      <span className="font-size-15px text-[#2F2F31] break-all font-Poppins-Regular block">
                        {item?.hospitalName || ""}
                      </span>
                    </div>
                    <div>
                      <span className="font-size-18px theme-color-green font-Poppins-Medium block">
                        Fees
                      </span>
                      <span className="font-size-15px text-[#2F2F31] font-Poppins-Regular block">
                      {CURRENCY} {item?.fees || ""}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {weekDays
                      ?.filter((v) => Object.keys(timings[index]).includes(v))
                      .map((day) => (
                        <div key={day} className="">
                          <span className="font-size-18px theme-color-green font-Poppins-Medium block">
                            {capitalize(day)}
                          </span>
                          {Array.isArray(
                            timings[index][day as keyof IAvailability]
                          )
                            ? (
                                timings[index][day as keyof IAvailability] as [
                                  string,
                                  string
                                ][]
                              ).map((item, i) => (
                                <span
                                  key={i}
                                  className="font-size-15px text-[#2F2F31] font-Poppins-Regular block"
                                >{`${moment(item[0], "HH:mm").format(
                                  "h:mm A"
                                )} to ${moment(item[1], "HH:mm").format(
                                  "h:mm A"
                                )}`}</span>
                              ))
                            : null}
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            ))
          : null}

        {createMode ? (
          <form onSubmit={handleSubmit} className="py-4">
            <div className="mt-4">
              <div>
                <SimpleInput
                  id="edit-hospital-availability-hospital-name"
                  type="text"
                  value={payload?.hospitalName || ""}
                  onChange={(val) => handleChangeItem("hospitalName", val)}
                  isLabel={true}
                  labelText={"Hospital Name"}
                  isRequired={true}
                  extraStyle={{ borderColor: "#009d91" }}
                  isLimit={60}
                  maxLength={60}
                />
              </div>

              <div>
                <SimpleInput
                  id="edit-hospital-availability-fees"
                  type="text"
                  value={payload?.fees || ""}
                  onChange={(val) => {
                    if (/^\d+$/.test(val) || val === "") {
                      handleChangeItem("fees", val);
                    }
                  }}
                  isLabel={true}
                  isRequired={true}
                  labelText={"Fee"}
                  maxLength={6}
                  extraStyle={{ borderColor: "#009d91" }}
                />
              </div>
              {/* <div className="flex flex-row gap-1 items-center">
                <SimpleInput
                  id="edit-hospital-availability-terms"
                  type="checkbox"
                  value={isTerms}
                  onChange={(val) => {
                    setIsTerms(val);
                  }}
                  isLabel={false}
                  isRequired={true}
                  extraStyle={{ borderColor: "#009d91", margin: 0 }}
                />
                <p className="font-size-14px font-Poppins-Regular ">
                  By proceeding, you agree that the platform commission will be
                  deducted from your set In-clinic appointment price.
                </p>
              </div> */}
              {weekDays.map((day) => (
                <div key={day} className="availability mt-4">
                  <div className="avail pb-4">
                    <div className="flex">
                      <ToggleRadioBtn
                        id={`edit-hospital-availability-toggle-${day}`}
                        toggleText={capitalize(day)}
                        isToggled={Object.keys(payload).includes(day)}
                        handleToggle={() =>
                          handleToggle(day as keyof IAvailability)
                        }
                      />
                    </div>
                    {Object.keys(payload).includes(day) && (
                      <>
                        {Array.isArray(payload[day as keyof IAvailability])
                          ? (
                              payload[day as keyof IAvailability] as [
                                string,
                                string
                              ][]
                            ).map((item, i) => (
                              <div
                                className="flex w-full gap-4 mt-4 items-center"
                                key={i}
                              >
                                <div className="w-[30%]">
                                  <SimpleInput
                                    id={`edit-hospital-availability-start-time-${day}-${i}`}
                                    value={item[0]}
                                    onChange={(val) =>
                                      handleChange(
                                        day as keyof IAvailability,
                                        i,
                                        0,
                                        val
                                      )
                                    }
                                    isLabel={true}
                                    labelText={"Start Time"}
                                    extraStyle={{ borderColor: "#009d91" }}
                                    type="time"
                                    isRequired={true}
                                  />
                                </div>

                                <div className="w-[30%]">
                                  <SimpleInput
                                    id={`edit-hospital-availability-end-time-${day}-${i}`}
                                    value={item[1]}
                                    onChange={(val) =>
                                      handleChange(
                                        day as keyof IAvailability,
                                        i,
                                        1,
                                        val
                                      )
                                    }
                                    isLabel={true}
                                    labelText={"End Time"}
                                    extraStyle={{ borderColor: "#009d91" }}
                                    type="time"
                                    isRequired={true}
                                  />
                                </div>
                                {i !== 0 && (
                                  <button
                                    id={`edit-hospital-availability-delete-btn-${day}-${i}`}
                                    onClick={() =>
                                      deleteRow(day as keyof IAvailability, i)
                                    }
                                    type="button"
                                    className="ml-4 mt-[35px]"
                                  >
                                    <FontAwesomeIcon icon={faTrash} />
                                  </button>
                                )}
                              </div>
                            ))
                          : null}
                        <div className="mt-4">
                          <button
                            id={`edit-hospital-availability-add-btn-${day}`}
                            onClick={() => addRow(day as keyof IAvailability)}
                            type="button"
                            className="font-Poppins-semiBold font-size-18px theme-color-green"
                          >
                            + Add another time
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-end mt-5 gap-4">
              <PrimaryBtn
                id="edit-hospital-availability-cancel-btn"
                type="button"
                ButtonText="Cancel"
                btnExtraStyle={{
                  borderRadius: "50rem",
                  backgroundColor: "#ff0000",
                  width: "7vw",
                }}
                onPress={cancelEdit}
              />
              <PrimaryBtn
                id="edit-hospital-availability-save-btn"
                type="submit"
                loader={isLoader}
                ButtonText="Save"
                btnExtraStyle={{
                  backgroundColor: "#004540",
                  borderRadius: "50rem",
                  width: "7vw",
                }}
              />
            </div>
          </form>
        ) : (
          <div className="my-5">
            <button
              id="edit-hospital-availability-add-btn"
              onClick={() => setCreateMode(true)}
              type="button"
              className="font-Poppins-semiBold font-size-18px theme-color-green"
            >
              + Add hospital
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default HospitalAvailabilityEdit;